import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useRepairContext } from "@/context/RepairContext";
import { Loader2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";

const RepairStatusWidget: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { counts, countsLoading } = useRepairContext();

  // Handle click on status cards
  const handleStatusClick = (status: string) => {
    // Navigate to search page with status filter
    navigate(`/search?status=${status}`);
  };

  if (countsLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const { byStatus, total } = counts;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div
          className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
          onClick={() => handleStatusClick("pending")}
          title={t("common.clickToView")}
        >
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {t("repair.status.pending")}
          </div>
          <div className="text-2xl font-bold">{byStatus.pending}</div>
          <Progress
            value={total > 0 ? (byStatus.pending / total) * 100 : 0}
            className="h-1 mt-2"
          />
        </div>
        <div
          className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg cursor-pointer hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors"
          onClick={() => handleStatusClick("inProgress")}
          title={t("common.clickToView")}
        >
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {t("repair.status.inProgress")}
          </div>
          <div className="text-2xl font-bold">{byStatus.inProgress}</div>
          <Progress
            value={total > 0 ? (byStatus.inProgress / total) * 100 : 0}
            className="h-1 mt-2 bg-yellow-100 dark:bg-yellow-900/30"
          />
        </div>
        <div
          className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg cursor-pointer hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
          onClick={() => handleStatusClick("completed")}
          title={t("common.clickToView")}
        >
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {t("repair.status.completed")}
          </div>
          <div className="text-2xl font-bold">{byStatus.completed}</div>
          <Progress
            value={total > 0 ? (byStatus.completed / total) * 100 : 0}
            className="h-1 mt-2 bg-green-100 dark:bg-green-900/30"
          />
        </div>
        <div
          className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg cursor-pointer hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
          onClick={() => handleStatusClick("returned")}
          title={t("common.clickToView")}
        >
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {t("repair.status.returned")}
          </div>
          <div className="text-2xl font-bold">{byStatus.returned}</div>
          <Progress
            value={total > 0 ? (byStatus.returned / total) * 100 : 0}
            className="h-1 mt-2 bg-purple-100 dark:bg-purple-900/30"
          />
        </div>
      </div>

      <div className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">
        {t("dashboard.totalRepairs")}:{" "}
        <span className="font-bold">{total}</span>
      </div>
    </div>
  );
};

export default RepairStatusWidget;
